import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Permission } from "../permissions/permission.enum";
import { useAuth } from "../context/AuthContext";

export interface NavItem {
  label: string;
  to?: string;
  permission?: Permission;
  icon?: React.ReactNode;
  children?: NavItem[];
  type?: "group" | "link";
}

const Sidebar = ({
  items,
  collapsed,
  onToggleCollapsed,
}: {
  items: NavItem[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) => {
  const { t } = useTranslation("common");
  const { hasPermission } = useAuth();

  const filterVisible = (item: NavItem): boolean =>
    !item.permission || hasPermission(item.permission);

  const visibleGroupsOrLinks = items
    .map((item) => {
      if (item.children && item.children.length > 0) {
        const visibleChildren = item.children.filter(filterVisible);
        return visibleChildren.length > 0
          ? { ...item, children: visibleChildren }
          : null;
      }
      return filterVisible(item) ? item : null;
    })
    .filter(Boolean) as NavItem[];

  return (
    <aside
      className={[
        "shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-[width] duration-200 ease-out flex flex-col",
        collapsed ? "w-16" : "w-64",
      ].join(" ")}
    >
      <div
        className={[
          "h-16 flex items-center text-gray-900 dark:text-gray-100 shrink-0",
          collapsed ? "px-2 justify-center" : "px-4 justify-between",
        ].join(" ")}
      >
        {!collapsed && <div className="font-semibold">{t("cms")}</div>}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>

      <nav
        className={[
          "py-2 space-y-1 flex-1 overflow-y-auto",
          collapsed ? "px-1" : "px-2",
        ].join(" ")}
      >
        {visibleGroupsOrLinks.map((item) =>
          item.children && item.children.length > 0 ? (
            <div key={item.label}>
              {!collapsed && (
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {item.label}
                </div>
              )}
              <div className="space-y-1">
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to!}
                    title={collapsed ? child.label : undefined}
                    className={({ isActive }) =>
                      [
                        "flex items-center rounded-md text-sm font-medium transition-colors",
                        collapsed ? "justify-center px-2 py-2" : "px-3 py-2",
                        isActive
                          ? "bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-white"
                          : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700",
                      ].join(" ")
                    }
                  >
                    {child.icon && (
                      <span className="text-lg" aria-hidden>
                        {child.icon}
                      </span>
                    )}
                    {!collapsed && <span className="ml-3">{child.label}</span>}
                    {collapsed && <span className="sr-only">{child.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to!}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                [
                  "flex items-center rounded-md text-sm font-medium transition-colors",
                  collapsed ? "justify-center px-2 py-2" : "px-3 py-2",
                  isActive
                    ? "bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700",
                ].join(" ")
              }
            >
              {item.icon && (
                <span className="text-lg" aria-hidden>
                  {item.icon}
                </span>
              )}
              {!collapsed && <span className="ml-3">{item.label}</span>}
              {collapsed && <span className="sr-only">{item.label}</span>}
            </NavLink>
          ),
        )}
      </nav>

      <div
        className={[
          "p-4 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 shrink-0",
          collapsed ? "text-center" : "text-right",
        ].join(" ")}
      >
        {collapsed ? "v1.0" : "Version 1.0.0"}
      </div>
    </aside>
  );
};

export default Sidebar;
